import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Select, Input, SimpleGrid, Spinner,
  Badge, Wrap, WrapItem, Stat, StatLabel, StatNumber, Alert, AlertIcon,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Table, Thead, Tbody, Tr, Th, Td, Icon,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Divider,
} from '@chakra-ui/react';
import { FiRefreshCw, FiMessageSquare, FiSearch, FiEye } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('assistant_token')}`,
});

const providerColor = (provider) => (provider === 'managed' ? 'purple' : 'teal');

const formatNumber = (n) => (n ?? 0).toLocaleString();

function UsageByTenant() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsage = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/usage/?days=${days}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(d => { setData(d); setError(null); })
      .catch(() => setError('Could not load usage stats.'))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Text color="gray.500">Token usage broken down by tenant, provider, and model.</Text>
        <HStack>
          <Select size="sm" value={days} onChange={(e) => setDays(Number(e.target.value))} width="140px">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </Select>
          <Button size="sm" leftIcon={<Icon as={FiRefreshCw} />} onClick={fetchUsage} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </HStack>

      {error && <Alert status="error" mb={4} borderRadius="md"><AlertIcon />{error}</Alert>}

      {loading && !data ? <Spinner size="xl" /> : data && (
        <>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
            <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
              <Stat>
                <StatLabel>Total Calls</StatLabel>
                <StatNumber>{formatNumber(data.totals.total_calls)}</StatNumber>
              </Stat>
            </Box>
            <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
              <Stat>
                <StatLabel>Input Tokens</StatLabel>
                <StatNumber>{formatNumber(data.totals.total_input_tokens)}</StatNumber>
              </Stat>
            </Box>
            <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
              <Stat>
                <StatLabel>Output Tokens</StatLabel>
                <StatNumber>{formatNumber(data.totals.total_output_tokens)}</StatNumber>
              </Stat>
            </Box>
          </SimpleGrid>

          <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Tenant</Th>
                  <Th isNumeric>Calls</Th>
                  <Th isNumeric>Input Tokens</Th>
                  <Th isNumeric>Output Tokens</Th>
                  <Th>By Provider / Model</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.tenants.map((t) => (
                  <Tr key={t.tenant_id ?? 'unknown'}>
                    <Td fontWeight="medium">{t.tenant_name}</Td>
                    <Td isNumeric>{formatNumber(t.total_calls)}</Td>
                    <Td isNumeric>{formatNumber(t.total_input_tokens)}</Td>
                    <Td isNumeric>{formatNumber(t.total_output_tokens)}</Td>
                    <Td>
                      <Wrap>
                        {t.by_model.map((m, i) => (
                          <WrapItem key={i}>
                            <Badge colorScheme={providerColor(m.provider)} fontSize="xs" px={2} py={1} borderRadius="md">
                              {m.model} · {formatNumber(m.calls)} calls · {formatNumber(m.input_tokens)}/{formatNumber(m.output_tokens)} tok
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Td>
                  </Tr>
                ))}
                {data.tenants.length === 0 && (
                  <Tr><Td colSpan={5} textAlign="center" color="gray.500">No usage recorded in this period</Td></Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}

function DiscussionDetail({ discussionId, onClose }) {
  const [discussion, setDiscussion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!discussionId) return;
    setLoading(true);
    fetch(`${API_BASE}/discussions/${discussionId}/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(setDiscussion)
      .finally(() => setLoading(false));
  }, [discussionId]);

  return (
    <Modal isOpen={!!discussionId} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Discussion #{discussionId}
          {discussion && <Text fontSize="sm" fontWeight="normal" color="gray.500">{discussion.tenant_name}</Text>}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {loading || !discussion ? <Spinner /> : (
            <VStack align="stretch" spacing={4}>
              {discussion.usage?.length > 0 && (
                <Wrap>
                  {discussion.usage.map((u, i) => (
                    <WrapItem key={i}>
                      <Badge colorScheme={providerColor(u.provider)} fontSize="xs" px={2} py={1} borderRadius="md">
                        {u.model} · {u.calls} calls · {formatNumber(u.input_tokens)}/{formatNumber(u.output_tokens)} tok
                      </Badge>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
              <Divider />
              <VStack align="stretch" spacing={3}>
                {discussion.messages.map((m) => (
                  <Box
                    key={m.id}
                    alignSelf={m.role === 'user' ? 'flex-end' : 'flex-start'}
                    maxW="85%"
                    bg={m.role === 'user' ? 'purple.500' : m.role === 'tool' ? 'gray.100' : 'gray.100'}
                    color={m.role === 'user' ? 'white' : 'gray.800'}
                    px={4}
                    py={2}
                    borderRadius="lg"
                  >
                    <HStack mb={1} spacing={2}>
                      <Badge colorScheme={m.role === 'user' ? 'whiteAlpha' : m.role === 'tool' ? 'gray' : 'purple'} fontSize="2xs">
                        {m.role}{m.tool_name ? `: ${m.tool_name}` : ''}
                      </Badge>
                      <Text fontSize="2xs" opacity={0.7}>{new Date(m.created_at).toLocaleString()}</Text>
                    </HStack>
                    <Text fontSize="sm" whiteSpace="pre-wrap" fontFamily={m.role === 'tool' ? 'mono' : 'inherit'}>
                      {m.content}
                    </Text>
                  </Box>
                ))}
                {discussion.messages.length === 0 && (
                  <Text color="gray.500" textAlign="center">No messages in this discussion</Text>
                )}
              </VStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function Discussions() {
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDiscussionId, setOpenDiscussionId] = useState(null);

  const fetchDiscussions = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page) });
    if (search) params.set('search', search);
    if (tenantId) params.set('tenant_id', tenantId);
    fetch(`${API_BASE}/discussions/?${params.toString()}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(d => { setResults(d.results || []); setPagination(d.pagination); setError(null); })
      .catch(() => setError('Could not load discussions.'))
      .finally(() => setLoading(false));
  }, [page, search, tenantId]);

  useEffect(() => { fetchDiscussions(); }, [fetchDiscussions]);

  return (
    <Box>
      <HStack mb={4} spacing={2}>
        <Input
          placeholder="Search message content..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          maxW="320px"
          size="sm"
        />
        <Input
          placeholder="Tenant ID"
          value={tenantId}
          onChange={(e) => { setTenantId(e.target.value.replace(/\D/g, '')); setPage(1); }}
          maxW="140px"
          size="sm"
        />
        <Button size="sm" leftIcon={<Icon as={FiSearch} />} onClick={fetchDiscussions} isLoading={loading}>
          Search
        </Button>
      </HStack>

      {error && <Alert status="error" mb={4} borderRadius="md"><AlertIcon />{error}</Alert>}

      <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Tenant</Th>
              <Th>Last Message</Th>
              <Th isNumeric>Messages</Th>
              <Th>Updated</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {results.map((d) => (
              <Tr key={d.id} _hover={{ bg: 'gray.50' }} cursor="pointer" onClick={() => setOpenDiscussionId(d.id)}>
                <Td>{d.id}</Td>
                <Td>{d.tenant_name}</Td>
                <Td maxW="360px" isTruncated>{d.last_message_preview || <Text as="span" color="gray.400">—</Text>}</Td>
                <Td isNumeric>{d.message_count}</Td>
                <Td fontSize="xs">{new Date(d.updated_at).toLocaleString()}</Td>
                <Td>
                  <Icon as={FiEye} color="gray.400" />
                </Td>
              </Tr>
            ))}
            {!loading && results.length === 0 && (
              <Tr><Td colSpan={6} textAlign="center" color="gray.500">No discussions found</Td></Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {pagination && pagination.pages > 1 && (
        <HStack mt={4} justify="center">
          <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page <= 1}>Prev</Button>
          <Text fontSize="sm">Page {pagination.page} of {pagination.pages}</Text>
          <Button size="sm" onClick={() => setPage(p => p + 1)} isDisabled={page >= pagination.pages}>Next</Button>
        </HStack>
      )}

      <DiscussionDetail discussionId={openDiscussionId} onClose={() => setOpenDiscussionId(null)} />
    </Box>
  );
}

function Usage() {
  return (
    <Box>
      <Heading size="lg" mb={6}>Usage &amp; Discussions</Heading>
      <Tabs colorScheme="purple">
        <TabList>
          <Tab>Usage by Tenant</Tab>
          <Tab><Icon as={FiMessageSquare} mr={2} />Discussions</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}><UsageByTenant /></TabPanel>
          <TabPanel px={0}><Discussions /></TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default Usage;
